import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/atoms/skeleton";
import { WALLET_HEIGHT } from "@/stripe/appearance";
import { cx } from "@/utils/cx";
import type { CreateCheckoutPaymentData } from "@/data/checkout";

/**
 * Apple Pay or Google Pay, first (checkout 1.4 region `platformPay`; spec platform-pay-button
 * 1.2).
 *
 * The wallet comes before the card because most people never touch a field, and it is ink with
 * the platform's own mark, never orange: the one orange on this page is Pay at the bottom. One
 * platform per device, whichever the browser offers, and the divider under it only when there is
 * something above it to divide from.
 *
 * Three states and all three are drawn. While Stripe decides, the band is the 48 skeleton at the
 * real height, so the row does not jump when the button lands. When Stripe says a wallet is
 * there, the button. When Stripe says there is none, the band and the divider go together: no
 * placeholder and no sentence announcing an absence (platform-pay-button 1.2 doNot 2).
 *
 * The answer can also never come — an http origin, where Stripe refuses both wallets outright,
 * or a domain not registered for Apple Pay. After the slow duration the band concludes there is
 * no wallet rather than holding a skeleton under the price for the whole session.
 */

export interface WalletBillingDetails {
  email?: string;
  name?: string;
  phone?: string;
  address?: CreateCheckoutPaymentData["address"];
}

export interface CheckoutWalletProps {
  isSubscription: boolean;
  collectPhoneNumber: boolean;
  collectBillingAddress: boolean;
  /** null while Stripe decides, false when this device offers neither. */
  available: boolean | null;
  onAvailable: (available: boolean) => void;
  onConfirm: (billing: WalletBillingDetails, failed: () => void) => void;
  /** A total of zero has nothing to pay with. */
  hidden?: boolean;
}

const WALLET_ANSWER_MS = 3000;

export function CheckoutWallet({
  isSubscription,
  collectPhoneNumber,
  collectBillingAddress,
  available,
  onAvailable,
  onConfirm,
  hidden = false,
}: CheckoutWalletProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (available !== null) return;
    const timer = window.setTimeout(() => onAvailable(false), WALLET_ANSWER_MS);
    return () => window.clearTimeout(timer);
  }, [available, onAvailable]);

  if (hidden || available === false) return null;

  return (
    <div className="gap-gk-4 flex flex-col">
      <div
        className={cx(
          "ds-pay-wallet relative",
          available === null && "isolate",
          available === true && "ds-pay-wallet-ready",
        )}
      >
        {available === null && (
          <Skeleton
            className="pointer-events-none absolute inset-0 z-10"
            style={{ height: WALLET_HEIGHT }}
          />
        )}
        <ExpressCheckoutElement
          options={{
            paymentMethods: {
              applePay: "auto",
              googlePay: "auto",
              link: "never",
              paypal: "never",
              amazonPay: "never",
              klarna: "never",
            },
            buttonTheme: { applePay: "black", googlePay: "black" },
            buttonType: {
              applePay: isSubscription ? "subscribe" : "plain",
              googlePay: isSubscription ? "subscribe" : "plain",
            },
            buttonHeight: WALLET_HEIGHT,
            layout: { maxColumns: 1, maxRows: 1, overflow: "never" },
            emailRequired: true,
            phoneNumberRequired: collectPhoneNumber,
            billingAddressRequired: collectBillingAddress,
          }}
          onReady={(event) => {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.info(
                "[pay] express checkout ready",
                event.availablePaymentMethods ??
                  "no wallet on this device or account",
              );
            }
            onAvailable(!!event.availablePaymentMethods);
          }}
          onConfirm={(event) =>
            onConfirm(
              {
                email: event.billingDetails?.email ?? undefined,
                name: event.billingDetails?.name ?? undefined,
                phone: event.billingDetails?.phone ?? undefined,
                address: fromWalletAddress(event.billingDetails?.address),
              },
              () => event.paymentFailed({ reason: "fail" }),
            )
          }
        />
      </div>
      {available && (
        <p className="ds-pay-divider">{t("gymkeePay.common.orPayByCard")}</p>
      )}
    </div>
  );
}

/**
 * The wallet's address, only when it is complete.
 *
 * A wallet hands back whatever the platform had; a half address posted to the backend is worse
 * than none, because the backend would store it as the customer's.
 */
function fromWalletAddress(
  address:
    | {
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        state?: string | null;
        postal_code?: string | null;
        country?: string | null;
      }
    | undefined,
): CreateCheckoutPaymentData["address"] {
  if (
    !address?.line1 ||
    !address.city ||
    !address.postal_code ||
    !address.country
  ) {
    return undefined;
  }
  return {
    line1: address.line1,
    line2: address.line2 ?? undefined,
    city: address.city,
    state: address.state ?? undefined,
    postal_code: address.postal_code,
    country: address.country,
  };
}
